import { Combobox } from "@dashboard/components/Combobox";
import FormSpacer from "@dashboard/components/FormSpacer";
import useProductSearch from "@dashboard/searches/useProductSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { sectionMessages } from "../../messages";

interface WsmProductFieldProps {
  value: string;
  /**
   * A product picked before this screen opened, so the field shows its name
   * without waiting for a search that may never be typed.
   */
  knownProduct?: { id: string; name?: string | null } | null;
  disabled?: boolean;
  error?: string;
  onChange: (productId: string) => void;
}

/**
 * Every compose row hangs off exactly one product, so every create screen picks
 * one here. Option sets, fees and compliance all key on the same field.
 */
export const WsmProductField = ({
  value,
  knownProduct,
  disabled,
  error,
  onChange,
}: WsmProductFieldProps) => {
  const intl = useIntl();
  /**
   * What the merchant picked HERE, remembered for the same reason knownProduct
   * exists: the options list is only ever the current search, so a picked product
   * would otherwise drop out of the field as soon as another query is typed.
   */
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const { search, result } = useProductSearch({
    variables: { after: null, first: 20, query: "" },
  });
  const searched = (mapEdgesToItems(result.data?.search) ?? []).map(product => ({
    label: product.name,
    value: product.id,
  }));
  const seed = picked?.id === value ? picked : knownProduct;
  const options =
    seed?.id && !searched.some(option => option.value === seed.id)
      ? [{ label: seed.name || seed.id, value: seed.id }, ...searched]
      : searched;

  return (
    <>
      <Combobox
        name="product"
        label={intl.formatMessage(sectionMessages.product)}
        data-test-id="wsm-compose-product-field"
        disabled={disabled}
        options={options}
        value={options.find(option => option.value === value) ?? null}
        loading={result.loading}
        fetchOptions={search}
        onChange={event => {
          const option = event.target.value
            ? options.find(candidate => candidate.value === event.target.value)
            : null;

          setPicked(option ? { id: option.value, name: option.label } : null);
          onChange(event.target.value ?? "");
        }}
        error={!!error}
        helperText={error ?? intl.formatMessage(sectionMessages.productHelp)}
        alwaysFetchOnFocus
      />
      <FormSpacer />
    </>
  );
};

export default WsmProductField;
